import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchBusinessRelocationRfqForSubmittedView } from "../../services/businessRelocationRfq.service";
import { fetchExhibitionLogisticsRfqForSubmittedView } from "../../services/exhibitionLogisticsRfq.service";
import { resolveLogisticsSubmittedRfqNavContext } from "../../utils/logisticsRfqPostSubmitNavigate";
import BusinessRelocationRfqSubmittedView from "./BusinessRelocationRfqSubmittedView";
import ExhibitionLogisticsRfqSubmittedView from "./ExhibitionLogisticsRfqSubmittedView";
import "../../assets/styles/business_relocation_rfq.scss";
import "../../assets/styles/business_relocation_rfq_submitted.scss";
import "../../assets/styles/exhibition_logistics_rfq.scss";
import "../../assets/styles/exhibition_logistics_rfq_submitted.scss";

const KIND_META = {
  "exhibition-logistics": {
    title: "Exhibition Logistics RFQ",
    variant: "exhibition",
  },
  "business-relocation": {
    title: "Business Relocation RFQ",
    variant: "relocation",
  },
};

function SubmittedRfqBreadcrumb({ navContext, variant = "relocation" }) {
  if (!navContext) return null;

  const isExhibition = variant === "exhibition";
  const rowClass = isExhibition
    ? "elrq-breadcrumb-row elrv-breadcrumb-row"
    : "lbrq-breadcrumb-row lbrv-breadcrumb-row";
  const navClass = isExhibition ? "elrq-breadcrumb" : "lbrq-breadcrumb";
  const sepClass = isExhibition ? "elrq-breadcrumb-sep" : "lbrq-breadcrumb-sep";
  const currentClass = isExhibition ? "elrq-breadcrumb-current" : "lbrq-breadcrumb-current";
  const backClass = isExhibition ? "elrq-back-link" : "lbrq-back-link";

  return (
    <div className={rowClass}>
      <nav className={navClass} aria-label="Breadcrumb">
        <Link to={navContext.dashboardHref}>{navContext.dashboardLabel || "Dashboard"}</Link>
        <span className={sepClass}>&gt;</span>
        <Link to={navContext.listHref}>{navContext.listLabel}</Link>
        <span className={sepClass}>&gt;</span>
        <span className={currentClass}>{navContext.breadcrumbCurrent}</span>
      </nav>
      <Link to={navContext.listHref} className={backClass}>
        ← Back
      </Link>
    </div>
  );
}

export default function LogisticsDedicatedRfqSubmittedPage() {
  const { kind, id } = useParams();
  const location = useLocation();
  const meta = KIND_META[kind];
  const navContext = useMemo(
    () =>
      resolveLogisticsSubmittedRfqNavContext({
        kind,
        pathname: location.pathname,
        search: location.search,
        state: location.state,
      }),
    [kind, location.pathname, location.search, location.state],
  );
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const showStaffDetails =
    navContext?.portal === "super-admin" || navContext?.portal === "logistics-manager";

  useEffect(() => {
    if (!meta || !id) {
      setError("Invalid RFQ link.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchRfq =
          kind === "exhibition-logistics"
            ? fetchExhibitionLogisticsRfqForSubmittedView
            : fetchBusinessRelocationRfqForSubmittedView;
        const rfq = await fetchRfq(id);
        if (!cancelled) setPayload(rfq);
      } catch (e) {
        if (!cancelled) {
          setPayload(null);
          setError(
            e?.response?.data?.message || e?.message || "Could not load this RFQ.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meta, id, kind]);

  if (!meta) {
    return (
      <div className="elrv-page">
        <p className="elrv-error">Unknown RFQ type.</p>
        <Link to="/logistics-rfqs-sent" className="elrq-back-link">
          ← Back to Logistics RFQs Sent
        </Link>
      </div>
    );
  }

  const pageClass = meta.variant === "exhibition" ? "elrv-page" : "lbrv-page";
  const loadingClass = meta.variant === "exhibition" ? "elrv-loading" : "lbrv-loading";
  const errorClass = meta.variant === "exhibition" ? "elrv-error" : "lbrv-error";

  if (kind === "business-relocation") {
    return (
      <div className={pageClass}>
        <SubmittedRfqBreadcrumb navContext={navContext} variant={meta.variant} />
        {loading ? <div className={loadingClass}>Loading RFQ…</div> : null}
        {!loading && error ? <div className={errorClass}>{error}</div> : null}
        {!loading && !error && payload ? (
          <BusinessRelocationRfqSubmittedView payload={payload} showStaffDetails={showStaffDetails} />
        ) : null}
      </div>
    );
  }

  if (kind === "exhibition-logistics") {
    return (
      <div className={pageClass}>
        <SubmittedRfqBreadcrumb navContext={navContext} variant={meta.variant} />
        {loading ? <div className={loadingClass}>Loading RFQ…</div> : null}
        {!loading && error ? <div className={errorClass}>{error}</div> : null}
        {!loading && !error && payload ? (
          <ExhibitionLogisticsRfqSubmittedView payload={payload} showStaffDetails={showStaffDetails} />
        ) : null}
      </div>
    );
  }

  return null;
}
